/** Inventory (I): bag grid with filters and sort, battle item slots, item actions and wallet. */
import { useRef, useState } from 'preact/hooks';
import { hasZone } from '../../content';
import { countOf, equip, isUpgrade, salvage, sellItem, sellStack, slotsUsed } from '../../game/inventory';
import { ITEMS } from '../../game/items';
import { api } from '../../ui/app/api';
import { openPanel, ui } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import { type Ctx, ItemCell, Money, commit, confirmThen, notify, useCtx } from './common';
import { keyTrap } from './keys';
import {
  BATTLE_SLOT_COUNT,
  INV_TABS,
  type InvEntry,
  type InvTab,
  assignBattleSlot,
  battleCategoryOf,
  canSalvage,
  canSell,
  clearBattleSlot,
  equipTypeLabel,
  filterEntries,
  fmt,
  fuseCost,
  inventoryEntries,
  primaryAction,
  sellPrice,
  sortInventory,
  stackTypeLabel,
  useStack,
} from './logic';
import { hideTip, tip } from './Tooltip';

const COLS = 10;
const DRAG_TYPE = 'application/x-af-item';

const entryName = (e: InvEntry): string => (e.type === 'equip' ? e.item.name : e.def.name);

function hintsFor(e: InvEntry): string[] {
  switch (primaryAction(e)) {
    case 'equip':
      return ['Right-click to equip'];
    case 'read':
      return ['Right-click to read'];
    case 'use':
      return ['Right-click to use'];
    case 'assign':
      return ['Right-click or drag to a battle slot (1–4)'];
    case 'socket':
      return ['Right-click to socket (Sigils › Gems)'];
    default:
      return [];
  }
}

/** Performs an entry's action. Returns true when something happened. */
function perform(ctx: Ctx, e: InvEntry, action = primaryAction(e), slot?: number): boolean {
  const { p, c } = ctx;
  switch (action) {
    case 'equip': {
      if (e.type !== 'equip') return false;
      if (!equip(c, e.item.uid)) return false;
      notify(`Equipped ${e.item.name}`, `var(--r-${e.item.rarity})`);
      commit({ stats: true });
      return true;
    }
    case 'read':
    case 'use': {
      if (e.type !== 'stack') return false;
      const r = useStack(p, e.id, { canTravel: hasZone });
      notify(r.text, r.color);
      commit({ stats: true });
      if (r.travel) api().travel(r.travel.zone, r.travel.spawn);
      return r.ok;
    }
    case 'assign': {
      if (e.type !== 'stack') return false;
      const next = assignBattleSlot(c.battleSlots, e.id, slot);
      if (!next) return false;
      // Per-instance use limits are tracked by Progression; a newly slotted item starts at its limit.
      c.battleSlots = next;
      const at = next.indexOf(e.id);
      notify(`${e.def.name} → battle slot ${at + 1}`, '#9ad0ff');
      commit();
      return true;
    }
    case 'socket':
      openPanel('sigils', 'gems');
      return true;
    default:
      return false;
  }
}

function sell(ctx: Ctx, e: InvEntry): void {
  const price = sellPrice(e);
  const label = e.type === 'equip' ? e.item.name : `${e.def.name}${e.count > 1 ? ` ×${e.count}` : ''}`;
  confirmThen(`Sell ${label} for ${fmt(price)} silver?`, 'Sell', () => {
    const got = e.type === 'equip' ? sellItem(ctx.c, ctx.a, e.item.uid) : sellStack(ctx.c, ctx.a, e.id, countOf(ctx.c, e.id));
    if (got > 0) notify(`Sold ${label} for ${fmt(got)} silver`, '#d8dee4');
    commit();
  });
}

function salvageEntry(ctx: Ctx, e: InvEntry): void {
  if (e.type !== 'equip') return;
  confirmThen(`Salvage ${e.item.name} into Aether Shards? This destroys the item.`, 'Salvage', () => {
    const n = salvage(ctx.c, e.item.uid);
    if (n > 0) notify(`Salvaged into ${fmt(n)} Aether Shards`, '#7ad8ff');
    commit();
  });
}

function ActionBar({ ctx, entry }: { ctx: Ctx; entry: InvEntry | null }) {
  if (!entry) {
    return (
      <div class="binv-act is-empty">
        <span>Select an item to see what you can do with it.</span>
        <span class="binv-act-keys">Right-click: equip / use · Drag battle items to 1–4</span>
      </div>
    );
  }
  const action = primaryAction(entry);
  const gem = entry.type === 'stack' && entry.def.kind === 'gem' ? entry.def : null;
  const fusable = gem && gem.level && gem.level < 7 && entry.type === 'stack' && entry.count >= 3;
  const type = entry.type === 'equip' ? equipTypeLabel(entry.item) : stackTypeLabel(entry.def);
  return (
    <div class="binv-act">
      <div class="binv-act-id">
        <span class={`binv-act-name r-${entry.rarity}`}>
          {entry.type === 'equip' && entry.item.kind === 'gear' && entry.item.plus > 0 ? <b class="binv-plus">+{entry.item.plus} </b> : null}
          {entryName(entry)}
        </span>
        <span class="binv-act-type">
          {type}
          {entry.type === 'stack' && entry.count > 1 ? ` · ×${fmt(entry.count)}` : ''}
        </span>
      </div>
      <div class="binv-act-btns">
        {action === 'equip' && (
          <button type="button" class="btn btn--gold" onClick={() => perform(ctx, entry)}>
            Equip
          </button>
        )}
        {(action === 'use' || action === 'read') && (
          <button type="button" class="btn btn--gold" onClick={() => perform(ctx, entry)}>
            {action === 'read' ? 'Read' : 'Use'}
          </button>
        )}
        {action === 'assign' && (
          <span class="binv-assign" role="group" aria-label="Assign to battle slot">
            {Array.from({ length: BATTLE_SLOT_COUNT }, (_, i) => (
              <button key={i} type="button" class={`btn binv-assign-b ${ctx.c.battleSlots[i] === (entry.type === 'stack' ? entry.id : '') ? 'is-on' : ''}`} onClick={() => perform(ctx, entry, 'assign', i)} aria-label={`Assign to slot ${i + 1}`}>
                {i + 1}
              </button>
            ))}
          </span>
        )}
        {action === 'socket' && (
          <button type="button" class="btn" onClick={() => perform(ctx, entry)}>
            Socket…
          </button>
        )}
        {fusable && gem.level && (
          <button
            type="button"
            class="btn"
            disabled={ctx.a.wallet.silver < fuseCost(gem.level)}
            {...tip(() => <div class="btt-note">Fuse three Lv.{gem.level} gems into one Lv.{(gem.level ?? 0) + 1} gem for {fmt(fuseCost(gem.level ?? 1))} silver.</div>)}
            onClick={() => {
              if (ctx.p.fuseGems(gem.ref === 'cd' ? 'cd' : 'dmg', gem.level ?? 1)) {
                notify(`Fused into ${ITEMS[`gem_${gem.ref}_${(gem.level ?? 0) + 1}`]?.name ?? 'a gem'}`, '#ffd070');
                commit();
              } else notify('Fusion failed: not enough gems or silver.', '#e5484d');
            }}
          >
            Fuse 3 → Lv.{gem.level + 1}
          </button>
        )}
        {canSell(entry) && (
          <button type="button" class="btn" onClick={() => sell(ctx, entry)}>
            Sell
          </button>
        )}
        {canSalvage(entry) && (
          <button type="button" class="btn" onClick={() => salvageEntry(ctx, entry)}>
            Salvage
          </button>
        )}
      </div>
    </div>
  );
}

function BattleSlots({ ctx, selected }: { ctx: Ctx; selected: InvEntry | null }) {
  const [over, setOver] = useState(-1);
  const { c } = ctx;
  const pending = selected?.type === 'stack' && battleCategoryOf(selected.id) ? selected : null;
  const drop = (i: number, id: string) => {
    const e = inventoryEntries(c).find((x) => x.type === 'stack' && x.id === id);
    if (e) perform(ctx, e, 'assign', i);
  };
  return (
    <div class="binv-battle">
      <span class="binv-battle-h">Battle Items</span>
      <div class="binv-battle-slots">
        {Array.from({ length: BATTLE_SLOT_COUNT }, (_, i) => {
          const id = c.battleSlots[i] ?? null;
          return (
            <div
              key={i}
              class={`binv-bslot ${over === i ? 'is-over' : ''} ${pending ? 'is-target' : ''}`}
              onDragOver={(ev) => {
                if (ev.dataTransfer?.types.includes(DRAG_TYPE)) {
                  ev.preventDefault();
                  setOver(i);
                }
              }}
              onDragLeave={() => setOver(-1)}
              onDrop={(ev) => {
                ev.preventDefault();
                setOver(-1);
                const dropped = ev.dataTransfer?.getData(DRAG_TYPE);
                if (dropped) drop(i, dropped);
              }}
            >
              <ItemCell
                item={id}
                count={id ? countOf(c, id) : undefined}
                size={44}
                label={id ? `Battle slot ${i + 1}: ${ITEMS[id]?.name ?? id}` : `Battle slot ${i + 1} (empty)`}
                tipProps={{ hints: ['Right-click to clear the slot'] }}
                class={id && countOf(c, id) === 0 ? 'is-out' : ''}
                onClick={() => {
                  if (pending) perform(ctx, pending, 'assign', i);
                }}
                onContextMenu={(ev) => {
                  ev.preventDefault();
                  if (!id) return;
                  c.battleSlots = clearBattleSlot(c.battleSlots, i);
                  hideTip();
                  commit();
                }}
              />
              <span class="binv-bkey">{i + 1}</span>
            </div>
          );
        })}
      </div>
      <span class="binv-battle-note">One potion, grenade, bomb and utility item each.</span>
    </div>
  );
}

export function InventoryPanel() {
  const ctx = useCtx();
  const arg = ui.panelArg.value ?? '';
  const [tab, setTab] = useState<InvTab>(arg.includes('gem') ? 'books' : 'all');
  const [sel, setSel] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  if (!ctx) return null;
  const { c, a, cls } = ctx;
  const entries = inventoryEntries(c);
  const shown = filterEntries(entries, tab);
  const cap = c.inventory.capacity;
  const cells = Math.max(cap, Math.ceil(shown.length / COLS) * COLS);
  const selected = shown.find((e) => e.key === sel) ?? entries.find((e) => e.key === sel) ?? null;
  const used = slotsUsed(c);

  const onGridKey = (ev: KeyboardEvent) => {
    const d = ev.key === 'ArrowRight' ? 1 : ev.key === 'ArrowLeft' ? -1 : ev.key === 'ArrowDown' ? COLS : ev.key === 'ArrowUp' ? -COLS : 0;
    if (!d) return;
    ev.preventDefault();
    const cur = Math.max(0, shown.findIndex((e) => e.key === sel));
    const next = Math.max(0, Math.min(shown.length - 1, cur + d));
    const e = shown[next];
    if (!e) return;
    setSel(e.key);
    (gridRef.current?.children[next] as HTMLElement | undefined)?.focus();
  };

  return (
    <Window title="Inventory" width={552} dock="right" className="bwin bwin--inv">
      <div class="binv" {...keyTrap}>
        <div class="binv-top">
          <div class="binv-tabs" role="tablist" aria-label="Item categories">
            {INV_TABS.map((t) => {
              const n = t.id === 'all' ? entries.length : filterEntries(entries, t.id).length;
              return (
                <button key={t.id} type="button" role="tab" class="tab binv-tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
                  {t.label}
                  {n > 0 && t.id !== 'all' && <span class="binv-tab-n">{n}</span>}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            class="btn binv-sort"
            onClick={() => {
              sortInventory(c);
              commit();
            }}
            {...tip(() => <div class="btt-note">Arrange items by type, rarity and level.</div>)}
          >
            Sort
          </button>
        </div>

        <div class="binv-grid" ref={gridRef} role="grid" aria-label="Bag" onKeyDown={onGridKey}>
          {Array.from({ length: cells }, (_, i) => {
            const e = shown[i];
            if (!e) return <ItemCell key={`empty-${i}`} class={i >= cap ? 'is-locked' : ''} tabIndex={-1} label={i >= cap ? 'Locked slot' : 'Empty slot'} />;
            const up = e.type === 'equip' && e.item.kind === 'gear' && isUpgrade(c, e.item);
            const isBattle = e.type === 'stack' && !!battleCategoryOf(e.id);
            return (
              <ItemCell
                key={e.key}
                item={e.type === 'equip' ? e.item : e.def}
                count={e.type === 'stack' ? e.count : undefined}
                weapon={cls?.weapon}
                selected={sel === e.key}
                tipProps={{ where: 'bag', count: e.type === 'stack' ? e.count : undefined, hints: hintsFor(e) }}
                badge={up ? <span class="bcell-up" aria-label="Upgrade">▲</span> : e.type === 'stack' && c.battleSlots.includes(e.id) ? <span class="bcell-slot">{c.battleSlots.indexOf(e.id) + 1}</span> : undefined}
                draggable={isBattle}
                onDragStart={(ev) => {
                  if (!isBattle || e.type !== 'stack') return;
                  hideTip();
                  ev.dataTransfer?.setData(DRAG_TYPE, e.id);
                  if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'move';
                }}
                onClick={() => setSel(sel === e.key ? null : e.key)}
                onDblClick={() => perform(ctx, e)}
                onContextMenu={(ev) => {
                  ev.preventDefault();
                  setSel(e.key);
                  perform(ctx, e);
                }}
              />
            );
          })}
        </div>

        <BattleSlots ctx={ctx} selected={selected} />
        <ActionBar ctx={ctx} entry={selected} />

        <footer class="binv-foot">
          <span class={`binv-cap ${used >= cap ? 'is-full' : ''}`} {...tip(() => <div class="btt-note">Each distinct stack and each piece of equipment takes one slot.</div>)}>
            <span class="binv-cap-bar">
              <i style={{ transform: `scaleX(${Math.min(1, used / Math.max(1, cap))})` }} />
            </span>
            {used} / {cap}
          </span>
          <Money silver={a.wallet.silver} gold={a.wallet.gold} class="binv-money" />
        </footer>
      </div>
    </Window>
  );
}

